import { QuickMoveEntry, useQuickMoveRawEvent } from '../QuickMove/QuickMoveEntry';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import React, { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { Col, getItemWithExpiration, Row } from '../../../lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Body3Regular, CaptionLight, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import { I_POPUP_EXIT } from '../../../types/images';
import dayjs from 'dayjs';
import WhiteSquareButton from '../../Button/WhiteSquareButton';
import ColorSquareButton from '../../Button/ColorSquareButton';
import {
    deleteGeneralScheduleAPI,
    deleteOffScheduleAPI,
    deleteRepeatGeneralScheduleAPI,
} from '../../../api/schedule';

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000047;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalInner = styled.div`
    background: #f5f9ff;
    width: 928px;
    height: 476px;
    border-radius: 8px;
    box-shadow: 8px 8px 32px 0px #0000007a;

    background-color: #fdfeff;
    display: flex;
    flex-direction: column;
`;

const HoverImage = styled.img`
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

// 403: 다른 센터 admin 이거나 읽기 전용(centerViewer) 계정 / 406: admin·superAdmin 이 아닌 role
// 둘 다 재시도로 풀리지 않으므로 '다시 시도' 안내를 하지 않는다.
const DELETE_PERMISSION_ERROR_MESSAGE = '일정을 삭제할 권한이 없습니다!\n계정 권한을 확인해주세요!';

// 강사 미발견과 기능 비활성(킬스위치 OFF)을 프론트가 구분할 방법이 없다.
// 어느 쪽이든 사용자가 재시도로 해결할 수 없으므로 재시도를 단정적으로 권하지 않는다.
const UNAVAILABLE_ERROR_MESSAGE = '요청을 처리할 수 없습니다\n잠시 후 다시 시도하거나 관리자에게 문의해주세요';

type Props = {
    modalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    navigate: any;
    detailObject: any;
    setDetailObject: any;
    createAlert: any;
    createTwoButtonAlert: any;
    authObject: any;
    calendarType: 'daily' | 'weekly';
    calendarSelectedDay: string;
    calendarScrollY: number;
};

const OffEtcDetailModal = ({
    modalVisible,
    setModalVisible,
    navigate,
    detailObject,
    setDetailObject,
    createAlert,
    createTwoButtonAlert,
    authObject,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
}: Props) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();
    const quickMoveEvent = useQuickMoveRawEvent(detailObject);
    /* 스케줄 동작 잠금(설계 §6): 일반 일정 「수정하기」=수정, 일반·OFF 「일정 삭제」(단건·반복)=삭제 */
    const editLock = useScheduleActionLock('schedule.edit');
    const deleteLock = useScheduleActionLock('schedule.delete');

    // 일반일정 삭제 API : (delete)
    const deleteGeneralSchedule = useMutation({
        mutationFn: async () => await deleteGeneralScheduleAPI(authObject?.centerId, detailObject?._id, authorization),
        onSuccess: (res) => {
            if (res?.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                setModalVisible(false);
            } else if (res?.status === 403 || res?.status === 406) {
                createAlert('권한 오류', DELETE_PERMISSION_ERROR_MESSAGE, true);
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // OFF일정 삭제 API : (delete)
    const deleteOffSchedule = useMutation({
        mutationFn: async () => await deleteOffScheduleAPI(authObject?.centerId, detailObject?.blockId, authorization),
        onSuccess: (res) => {
            if (res?.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                setModalVisible(false);
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // 일반일정 반복(번들) 삭제 API : (delete)
    // 서버는 이 일정의 시작 시각(anchor) 이후의 같은 번들 일정을 취소하며, 이 일정 자신도 함께 지워진다.
    // 기준은 "지금"이 아니라 "지금 열어둔 이 일정"이다 — 이미 지나간 회차를 열고 삭제하면
    // 그 회차부터 함께 지워진다. anchor 보다 앞선 회차만 남는다.
    const deleteRepeatGeneralSchedule = useMutation({
        mutationFn: async () =>
            await deleteRepeatGeneralScheduleAPI(
                authObject?.centerId,
                detailObject?._id,
                detailObject?.scheduleBundleId,
                authorization
            ),
        onSuccess: (res) => {
            // 확정 성공 코드는 200. 204 도 성공으로 받아들인다.
            if (res?.status === 200 || res?.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                setModalVisible(false);

                // 몇 건이 지워졌는지 알려준다. count 는 서버 배포 상태에 따라 없을 수 있어 있을 때만 쓴다.
                const deletedCount = Number(res?.data?.count);
                if (Number.isFinite(deletedCount) && deletedCount > 0) {
                    createAlert('삭제 완료', `총 ${deletedCount}건의 반복 일정이 삭제되었습니다`, false);
                }
            } else if (res?.status === 403 || res?.status === 406) {
                createAlert('권한 오류', DELETE_PERMISSION_ERROR_MESSAGE, true);
            } else if (res?.status === 404) {
                // 배포 중간 상태(신규 웹 + 킬스위치 OFF)에서도 404 가 온다. 사용자가 재시도로 풀 수 없다.
                createAlert('오류', UNAVAILABLE_ERROR_MESSAGE, true);
            } else {
                createAlert('오류', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
            }
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 삭제해주세요!', true);
        },
    });

    /**
     * 번들 삭제는 anchor 의 centerId 가 현재 센터일 때만 노출한다.
     *
     * 캘린더 조회(read)는 센터 귀속이 없는(centerId 가 빈) 일정까지 관대하게 포함하지만
     * (rappo `onetimeSchedule.service.js` 의 centerScopeFilter), 번들 삭제(write)는 fail-closed 라
     * anchor 의 centerId 가 비어 있으면 서버가 403 을 낸다. 어느 센터 번들인지 데이터로 증명할 수
     * 없기 때문이다. 조건을 걸지 않으면 사용자가 눌러야만 알 수 있는 403 을 보게 된다.
     */
    const scheduleCenterId = detailObject?.centerId ? String(detailObject.centerId) : '';
    const currentCenterId = authObject?.centerId ? String(authObject.centerId) : '';
    const isRepeatBundleDeletable =
        !!detailObject?.scheduleBundleId && scheduleCenterId !== '' && scheduleCenterId === currentCenterId;

    const openDelete = () => {
        if (detailObject?.type === 'onetimeSchedule') {
            // 1) 이 센터가 만든 반복 일정 : 이 일정만 지울지, 이후 일정까지 지울지 물어본다.
            if (isRepeatBundleDeletable) {
                if (typeof createTwoButtonAlert === 'function') {
                    // 두 버튼 모두 삭제를 실행하므로, 주 버튼(오른쪽)에 덜 파괴적인 '이 일정만 삭제'를 둔다.
                    // 반사적으로 주 버튼을 눌러도 최대 200건이 한 번에 날아가지 않게 하기 위함이다.
                    //
                    // 버튼 라벨은 ModalAlert 이 size="middle"(148px 고정폭)로 렌더한다. 공용 컴포넌트라
                    // 폭을 늘릴 수 없으므로 라벨을 폭에 맞춘다. Body2Regular 18px + 좌우 마진 8px 기준
                    // 실제 글자 자리는 130px 뿐이고, Noto Sans KR 한글 1자 = 18px 이라 한글 6자 + 공백
                    // 2개(=117px)가 한계다. '이후 일정 모두 삭제'(한글 8자 = 158px)는 넘쳐서 마지막
                    // 글자가 다음 줄로 밀렸다. '이후'라는 시점 개념만 남기고 줄인다.
                    createTwoButtonAlert(
                        '반복으로 등록된 일반일정이에요!',
                        '이 일정만 삭제할지,\n이 일정을 포함해 이후 회차까지 모두 삭제할지 선택해주세요\n이미 지나간 일정을 보고 있다면 그 회차도 함께 삭제됩니다',
                        true,
                        '이후 모두 삭제',
                        '이 일정만 삭제',
                        () => {
                            deleteLock.run(() => deleteRepeatGeneralSchedule.mutate());
                        },
                        () => {
                            deleteLock.run(() => deleteGeneralSchedule.mutate());
                        },
                        true
                    );
                    return;
                }
                // createTwoButtonAlert 가 없는 경우(프롭 미전달)에는 파괴적인 쪽을 고르지 않고 단건 삭제로 떨어뜨린다.
            } else if (detailObject?.scheduleBundleId) {
                // 2) 센터 귀속이 없는(centerId 가 빈) 반복 일정 : 번들 삭제가 403 이라 한 건만 지울 수 있다.
                //    기준은 "누가 만들었나"가 아니라 centerId 유무다. 강사가 만들었어도 센터 workspace 에서
                //    만들었으면 centerId 가 박혀 위 1) 분기로 들어간다(addOnetimeSchedule 이 issuerRole 을
                //    centerId 유무로 정한다). 그래서 문구에서 작성자를 단정하지 않는다.
                //    묻지 않고 지우면 "반복인데 왜 안 묻지" 또는 "다 지워진 줄 알았다"는 오해가 생기므로
                //    한 건만 지워진다는 사실과 나머지를 지우는 방법을 먼저 알린다.
                if (typeof createTwoButtonAlert === 'function') {
                    createTwoButtonAlert(
                        '한 번에 삭제할 수 없는 반복 일정이에요!',
                        '센터에 등록된 반복 일정이 아니라 이 일정 하나만 삭제됩니다\n나머지 반복 일정은 담당 강사가 앱에서 삭제할 수 있습니다',
                        true,
                        '취소',
                        '이 일정만 삭제',
                        () => {},
                        () => {
                            deleteLock.run(() => deleteGeneralSchedule.mutate());
                        }
                    );
                    return;
                }
                // 폴백도 동일하게 단건 삭제로 진행한다.
            }

            // 3) 반복이 아닌 일반 단건 일정 : 기존 동작대로 곧바로 삭제한다.
            deleteLock.run(() => deleteGeneralSchedule.mutate());
        } else {
            deleteLock.run(() => deleteOffSchedule.mutate());
        }
    };

    /* 누르면 먼저 묻는다(확인창은 인증 뒤에). 확인창 안의 실제 삭제는 보내기 직전에 다시 본다 */
    const onClickDeleteButton = () => deleteLock.run(openDelete);

    const onClickUpdateButton = () => {
        if (detailObject?.type === 'onetimeSchedule') {
            editLock.run(() =>
                navigate('/schedule/update', {
                    state: {
                        type: 'etcSchedule',
                        scheduleObject: detailObject,
                        calendarType,
                        calendarSelectedDay,
                        calendarScrollY,
                    },
                })
            );
        }
    };

    return (
        <ModalOverLay>
            <ModalInner>
                <Row>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4, marginLeft: 48, marginTop: 36 }}>
                        {detailObject?.type === 'onetimeSchedule' ? '일반 일정 자세히보기' : 'OFF 자세히보기'}
                    </Subtitle1Bold>
                    {detailObject?.type === 'onetimeSchedule' ? (
                        <Row
                            style={{
                                width: 112,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #17B5CB',
                                marginTop: 38,
                                marginLeft: 16,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Body3Regular style={{ color: colors.CYAN }}>일반 일정</Body3Regular>
                        </Row>
                    ) : (
                        <Row
                            style={{
                                width: 112,
                                height: 32,
                                borderRadius: 4,
                                backgroundColor: colors.WHITE_700,
                                marginTop: 38,
                                marginLeft: 16,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Body3Regular style={{ color: colors.LAVEL_0 }}>OFF</Body3Regular>
                        </Row>
                    )}

                    {/* 반복 일정이라는 사실을 삭제를 눌러야 알게 되는 상태를 없앤다. */}
                    {detailObject?.type === 'onetimeSchedule' && detailObject?.scheduleBundleId && (
                        <>
                            <Row
                                style={{
                                    width: 72,
                                    height: 32,
                                    borderRadius: 4,
                                    border: `1px solid ${colors.BLUE_500}`,
                                    marginTop: 38,
                                    marginLeft: 8,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Body3Regular style={{ color: colors.BLUE_500 }}>반복</Body3Regular>
                            </Row>
                            <CaptionLight style={{ color: colors.LAVEL_2, marginTop: 46, marginLeft: 8 }}>
                                {'수정은 이 회차에만 적용됩니다'}
                            </CaptionLight>
                        </>
                    )}

                    <HoverImage
                        onClick={() => {
                            setModalVisible(false);
                            setDetailObject(null);
                        }}
                        src={I_POPUP_EXIT}
                        style={{ marginTop: 16, width: 32, height: 32, marginLeft: 'auto', marginRight: 24 }}
                    />
                </Row>

                <Row style={{ marginTop: 48, alignItems: 'center' }}>
                    <Col style={{ width: 310, height: 68, marginLeft: 72, justifyContent: 'space-between' }}>
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>
                            {detailObject?.type === 'onetimeSchedule' ? '일정 제목' : '담당자'}
                        </Body3Regular>
                        <Row
                            style={{
                                width: 310,
                                height: 36,
                                borderRadius: 36,
                                border: '1px solid #ededed',
                                paddingLeft: 16,
                                backgroundColor: colors.WHITE_500,
                                alignItems: 'center',
                            }}
                        >
                            {detailObject?.type === 'onetimeSchedule' ? (
                                <Body3Regular style={{ color: colors.LAVEL_1 }}>{`${detailObject?.name}`}</Body3Regular>
                            ) : (
                                <Body3Regular
                                    style={{ color: colors.LAVEL_1 }}
                                >{`${detailObject?.coachName} 강사`}</Body3Regular>
                            )}
                        </Row>
                    </Col>

                    <Col style={{ width: 340, height: 68, marginLeft: 104, justifyContent: 'space-between' }}>
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>날짜</Body3Regular>
                        <Row
                            style={{
                                width: 340,
                                height: 36,
                                borderRadius: 36,
                                border: '1px solid #ededed',
                                paddingLeft: 16,
                                backgroundColor: colors.WHITE_500,
                                alignItems: 'center',
                            }}
                        >
                            <Body3Regular style={{ color: colors.LAVEL_1 }}>{`${dayjs(detailObject?.startTime).format(
                                'YYYY년 MM월 DD일 (ddd)'
                            )}`}</Body3Regular>
                        </Row>
                    </Col>
                </Row>

                <Row style={{ marginTop: 48, alignItems: 'center' }}>
                    <Col style={{ width: 310, height: 68, marginLeft: 72, justifyContent: 'space-between' }}>
                        {detailObject?.type === 'onetimeSchedule' && (
                            <>
                                <Body3Regular style={{ color: colors.LAVEL_4 }}>담당자</Body3Regular>
                                <Row
                                    style={{
                                        width: 310,
                                        height: 36,
                                        borderRadius: 36,
                                        border: '1px solid #ededed',
                                        paddingLeft: 16,
                                        backgroundColor: colors.WHITE_500,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Body3Regular
                                        style={{ color: colors.LAVEL_1 }}
                                    >{`${detailObject?.coachName}`}</Body3Regular>
                                </Row>
                            </>
                        )}
                    </Col>

                    <Col style={{ width: 340, height: 68, marginLeft: 104, justifyContent: 'space-between' }}>
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>시간</Body3Regular>
                        <Row
                            style={{
                                width: 340,
                                height: 36,
                                borderRadius: 36,
                                border: '1px solid #ededed',
                                paddingLeft: 16,
                                backgroundColor: colors.WHITE_500,
                                alignItems: 'center',
                            }}
                        >
                            <Body3Regular style={{ color: colors.LAVEL_1 }}>{`${dayjs(detailObject?.startTime).format(
                                'A hh시 mm분 ~ '
                            )}  ${dayjs(detailObject?.endTime).format('A hh시 mm분')}`}</Body3Regular>
                        </Row>
                    </Col>
                </Row>

                {detailObject?.type === 'onetimeSchedule' && (
                    <>
                        <Row
                            style={{
                                width: quickMoveEvent ? 460 : 312,
                                marginLeft: quickMoveEvent ? 432 : 580,
                                marginTop: 64,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            {quickMoveEvent && <QuickMoveEntry event={quickMoveEvent} onOpenSheet={() => setModalVisible(false)} />}
                            <WhiteSquareButton text="일정 삭제" size="middle" callBack={onClickDeleteButton} />
                            <ColorSquareButton text="수정하기" size="middle" callBack={onClickUpdateButton} />
                        </Row>
                    </>
                )}
                {detailObject?.type === 'block' && (
                    <>
                        <Row
                            style={{
                                width: 312,
                                marginLeft: 744,
                                marginTop: 64,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <WhiteSquareButton text="일정 삭제" size="middle" callBack={onClickDeleteButton} />
                        </Row>
                    </>
                )}
            </ModalInner>
            {editLock.gate}
            {deleteLock.gate}
        </ModalOverLay>
    );
};

export default OffEtcDetailModal;
